import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

const GREEN = '#2e9f86';

// Popup that lets the user pick a product's add-ons before it goes on the order.
// onAdd is called with { modifiers: [{name, price}], note, extra }.
export default function ModifierPicker({ product, onClose, onAdd }) {
  const { t } = useTranslation();
  const { data: allGroups = [] } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: () => api.get('/api/modifier-groups').then((r) => r.data),
  });

  const groupIds = (product.modifier_group_ids || []).map(String);
  const groups = allGroups.filter((g) => groupIds.includes(String(g._id)));

  const [selected, setSelected] = useState({}); // groupId -> [optionIndex,...]
  const [note, setNote] = useState('');

  const toggle = (g, optIdx) => {
    setSelected((s) => {
      const cur = s[g._id] || [];
      if (g.multi) {
        return { ...s, [g._id]: cur.includes(optIdx) ? cur.filter((i) => i !== optIdx) : [...cur, optIdx] };
      }
      return { ...s, [g._id]: cur.includes(optIdx) ? [] : [optIdx] }; // single choice
    });
  };

  const modifiers = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      (selected[g._id] || []).forEach((i) => {
        const o = g.options[i];
        if (o) out.push({ name: o.name, price: Number(o.price) || 0 });
      });
    });
    return out;
  }, [groups, selected]);

  const extra = modifiers.reduce((s, m) => s + m.price, 0);
  const canAdd = groups.every((g) => !g.required || (selected[g._id] || []).length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm" style={{ maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg">{product.name}</h3>

        {groups.map((g) => (
          <div key={g._id} className="mt-3">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">{g.name}{g.required ? ' *' : ''}</div>
            <div className="mt-1.5 space-y-1.5">
              {(g.options || []).map((o, i) => {
                const on = (selected[g._id] || []).includes(i);
                return (
                  <button key={i} onClick={() => toggle(g, i)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm"
                    style={on ? { borderColor: GREEN, background: '#f0fdf8', color: '#065f46' } : { borderColor: '#e5e7eb' }}>
                    <span className="font-semibold">{on ? '✓ ' : ''}{o.name}</span>
                    <span className="text-gray-500">{o.price ? `+$${Number(o.price).toFixed(2)}` : ''}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <label className="block text-xs font-semibold text-gray-500 mt-3 mb-1">{t('note', 'Note')}</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('notePlaceholder', 'e.g. well done')} className="w-full border rounded-lg px-3 py-2 mb-4 text-sm" />

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border font-semibold">{t('cancel', 'Cancel')}</button>
          <button disabled={!canAdd} onClick={() => onAdd({ modifiers, note, extra })}
            className="flex-1 py-2.5 rounded-lg font-bold text-white disabled:opacity-40" style={{ background: GREEN }}>
            {t('add', 'Add')}{extra > 0 ? ` +$${extra.toFixed(2)}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
